import React, { useState, useEffect, useMemo } from 'react';
import './PricelistItems.css';
import api from '../services/api';
import useColumnWidths from '../components/useColumnWidths';
import ColumnWidthsReset from '../components/ColumnWidthsReset';

/** Sloupce výpisu položek ceníku — klíče, pod kterými se ukládají šířky. */
const SLOUPCE = ['nazev', 'medium', 'format', 'jednotky', 'cena', 'maxKusu', 'stav', 'id', 'akce'];

function PricelistItems({ pricelistId, pricelistName, mediaName, mediaId, openTab }) {
  const [ratecard, setRatecard] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Šířky sloupců tažením — ukládají se uživateli do DB (migrace 048).
  const { wrapRef, colgroup, tableStyle, resizerProps, maUlozene, vychozi } =
    useColumnWidths('pricelist.items', SLOUPCE);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const result = await api.getRatecardItems(pricelistId);
        if (result.data) {
          setRatecard(result.data.ratecard || {});
          setItems(result.data.items || []);
        }
      } catch (err) {
        console.error('Failed to fetch ratecard items:', err);
      } finally {
        setLoading(false);
      }
    };

    if (pricelistId) {
      fetchItems();
    }
  }, [pricelistId]);

  // Sort items by name
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'cs'));
  }, [items]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Načítání...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* PAGE HEADER */}
      <div className="pi-page-header">
        <div>
          <h5 className="mb-0">
            <i className="bi bi-list-ul me-2"></i>
            Položky v ceníku: {pricelistName || ratecard.name || pricelistId}
          </h5>
          <small className="text-muted">
            {mediaName && `${mediaName} • `}
            ID: {ratecard.external_id || ratecard.id || pricelistId}
            {ratecard.is_available !== undefined && (
              ratecard.is_available ? ' • Aktivní' : ' • Neaktivní'
            )}
          </small>
        </div>
        <ColumnWidthsReset maUlozene={maUlozene} vychozi={vychozi} className="ms-auto me-2" />
        {openTab && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => openTab(
              `ratecard-edit-${pricelistId}`,
              `Upravit: ${pricelistName || ratecard.name || 'Ceník'}`,
              `/media/${mediaId || ''}/ratecard/${pricelistId}/edit`,
              'RatecardForm',
              { mediaId, mediaName, ratecardId: pricelistId }
            )}
          >
            <i className="bi bi-pencil me-1"></i>Upravit ceník
          </button>
        )}
      </div>

      {/* TABLE */}
      <div className="card card-tile">
        <div className="card-body p-0">
          <div className="table-responsive grid-wrap" ref={wrapRef}>
            <table className="table table-sm pi-table table-hover align-middle mb-0 grid-resizable" style={tableStyle}>
              {colgroup && (
                <colgroup>
                  {colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
                </colgroup>
              )}
              <thead className="table-light">
                <tr>
                  <th style={{ width: '22%' }}>Název položky<span {...resizerProps('nazev')} /></th>
                  <th style={{ width: '13%' }}>Médium<span {...resizerProps('medium')} /></th>
                  <th style={{ width: '14%' }}>Formát<span {...resizerProps('format')} /></th>
                  <th style={{ width: '11%' }}>Jednotky<span {...resizerProps('jednotky')} /></th>
                  <th style={{ width: '12%' }}>Cena za jednotku<span {...resizerProps('cena')} /></th>
                  <th style={{ width: '7%' }}>Max. kusů<span {...resizerProps('maxKusu')} /></th>
                  <th style={{ width: '7%' }}>Stav<span {...resizerProps('stav')} /></th>
                  <th style={{ width: '9%' }}>ID<span {...resizerProps('id')} /></th>
                  <th style={{ width: '5%' }} className="text-end">Akce<span {...resizerProps('akce')} /></th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map(item => {
                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="pi-item-name">{item.name}</div>
                      </td>
                      <td>{item.section_name || '—'}</td>
                      <td>{item.format_label || '—'}</td>
                      <td>{item.units_label || item.units || '—'}</td>
                      <td>{item.price_per_unit || '—'}</td>
                      <td>
                        {item.max_count === 0 ? (
                          <span className="badge pi-badge-infinity">
                            <i className="bi bi-infinity"></i>
                          </span>
                        ) : (
                          <span className="badge pi-badge-max">max {item.max_count}</span>
                        )}
                      </td>
                      <td>
                        {item.is_available ? (
                          <span className="badge bg-success">Aktivní</span>
                        ) : (
                          <span className="badge bg-secondary">Neaktivní</span>
                        )}
                      </td>
                      <td className="text-muted" style={{ fontSize: '0.85rem' }}>{item.external_id || item.id}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-secondary btn-action"
                          title="Upravit ceník"
                          onClick={() => openTab && openTab(
                            `ratecard-edit-${pricelistId}`,
                            `Upravit: ${pricelistName || ratecard.name || 'Ceník'}`,
                            `/media/${mediaId || ''}/ratecard/${pricelistId}/edit`,
                            'RatecardForm',
                            { mediaId, mediaName, ratecardId: pricelistId }
                          )}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {sortedItems.length === 0 && (
                  <tr>
                    <td colSpan="9" className="text-center text-muted py-4">
                      Žádné položky v tomto ceníku
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default PricelistItems;
